<template>
  <div>
    <div class="mt-4">
      <div :class="table({ class: 'grid-cols-[repeat(8,minmax(max-content,1fr))]' })">
        <SortableTableHeader
          v-model:sort-column="sortColumn"
          v-model:sort-direction="sortDirection"
        >
          <SortableTableHeaderCell column="customer">{{ t('customer') }}</SortableTableHeaderCell>
          <SortableTableHeaderCell column="kind">{{ t('kind') }}</SortableTableHeaderCell>
          <SortableTableHeaderCell column="invoiceType">
            {{ t('invoiceType') }}
          </SortableTableHeaderCell>
          <SortableTableHeaderCell column="booking">{{ t('booking') }}</SortableTableHeaderCell>
          <SortableTableHeaderCell column="invoice">{{ t('invoice') }}</SortableTableHeaderCell>
          <SortableTableHeaderCell column="duePeriod" numeric>
            {{ t('duePeriod') }}
          </SortableTableHeaderCell>
          <SortableTableHeaderCell column="totalDue" numeric>
            {{ t('totalDue') }}
          </SortableTableHeaderCell>
          <SortableTableHeaderCell column="dueSince">{{ t('dueSince') }}</SortableTableHeaderCell>
        </SortableTableHeader>
        <div
          v-for="item in sortedItems"
          :key="rowKey(item)"
          :class="tableRow({ class: 'group/row' })"
        >
          <div :class="tableCell()">{{ formatCustomerName(item) }}</div>
          <div :class="tableCell()">
            {{ item.invoiceId ? t('invoice') : t('booking') }}
          </div>
          <div :class="tableCell()">
            {{
              item.invoiceType ? tGlobal(`invoiceTypes.${item.invoiceType}`, item.invoiceType) : '-'
            }}
          </div>
          <div :class="tableCell()">
            <router-link
              v-if="item.bookingId"
              :to="{ name: 'bookingEdit', params: { id: item.bookingId } }"
              class="text-link"
            >
              {{ item.bookingNumber ?? '-' }}
            </router-link>
            <span v-else>-</span>
          </div>
          <div :class="tableCell()">
            <div class="flex items-center gap-2">
              <router-link
                v-if="item.invoiceId"
                :to="{ name: 'invoice', params: { id: item.invoiceId } }"
                class="text-link"
              >
                {{ item.completeInvoiceNumber ?? '-' }}
              </router-link>
              <span v-else>-</span>
              <FloatingComments
                v-if="item.invoiceId"
                entity-type="Invoice"
                :entity-id="item.invoiceId"
              />
            </div>
          </div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(item.duePeriodAmount) }}
          </div>
          <div :class="tableCell({ numeric: true })">{{ formatCurrency(item.dueNowAmount) }}</div>
          <div :class="tableCell()">
            {{ item.dueSince ? formatDate(item.dueSince, DateTime.DATE_SHORT) : '-' }}
          </div>
        </div>
      </div>
      <div class="px-4">
        <EntityListExceptions
          :status="itemsQuery.status.value ?? 'pending'"
          :error="itemsQuery.error.value ?? null"
          :items-count="sortedItems.length"
          :no-items-text="t('noReceivables')"
          small
        />
      </div>
      <div class="mt-4 flex justify-end">
        <CVButton variant="secondary" :disabled="!sortedItems.length" @click="handleExportCsv">
          {{ t('exportCsv') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { refDebounced } from '@vueuse/core';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import SortableTableHeader from '@/components/SortableTableHeader.vue';
import SortableTableHeaderCell from '@/components/SortableTableHeaderCell.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import { downloadBlob } from '@/utils/download-file';
import { useReceivablesItems } from '@/queries/use-receivables';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { tableCell, tableRow, table } from '@carvia/theme/lib/cva/grid';
import type {
  ReceivablesControllerGetItemsData,
  ReceivablesItemDto,
} from '@carvia/ros-client-types';
import FloatingComments from '@/components/comments/FloatingComments.vue';
import {
  receivablesBooleanQueryParam,
  receivablesInvoiceTypesParam,
} from '@/views/receivables/receivables-query-filters';

type ReceivablesItem = ReceivablesItemDto & {
  dueSince?: Date | string | null;
};

const props = defineProps<{
  search: string;
  periodicFilter: string | null;
  invoicedFilter: string | null;
  depositFilter: string | null;
  selectedInvoiceTypes: string[];
  dueDays: number;
  isCredit: boolean;
  rowKey: (item: ReceivablesItem) => string;
  formatCustomerName: (item: ReceivablesItem) => string;
}>();

const { t } = useI18n();
const { t: tGlobal } = useI18n({ useScope: 'global' });

const hasPeriodicInvoicing = computed(() => receivablesBooleanQueryParam(props.periodicFilter));

const isDeposit = computed(() => receivablesBooleanQueryParam(props.depositFilter));

const debouncedSearch = refDebounced(toRef(props, 'search'), 400);

const queryInvoiceTypes = computed(() =>
  receivablesInvoiceTypesParam(props.invoicedFilter, props.selectedInvoiceTypes),
);

const queryParams = computed<ReceivablesControllerGetItemsData['query']>(() => ({
  dueDays: props.dueDays,
  invoiceTypes:
    queryInvoiceTypes.value as unknown as ReceivablesControllerGetItemsData['query']['invoiceTypes'],
  hasPeriodicInvoicing: hasPeriodicInvoicing.value,
  isDeposit: isDeposit.value,
  isCredit: props.isCredit,
  search: debouncedSearch.value || undefined,
}));

const itemsQuery = useReceivablesItems(queryParams);

const SORT_COLUMNS = [
  'customer',
  'kind',
  'invoiceType',
  'booking',
  'invoice',
  'duePeriod',
  'totalDue',
  'dueSince',
] as const;
type SortColumn = (typeof SORT_COLUMNS)[number];

const sortColumn = useRouteQueryRef('sort', '', 'string');
const sortDirection = useRouteQueryRef<'asc' | 'desc'>('sortDir', 'asc', 'string');

const sortValue = (item: ReceivablesItem, column: SortColumn): string | number | null => {
  switch (column) {
    case 'customer':
      return props.formatCustomerName(item);
    case 'kind':
      return item.invoiceId ? t('invoice') : t('booking');
    case 'invoiceType':
      return item.invoiceType
        ? tGlobal(`invoiceTypes.${item.invoiceType}`, item.invoiceType)
        : null;
    case 'booking':
      return item.bookingNumber ?? null;
    case 'invoice':
      return item.completeInvoiceNumber ?? null;
    case 'duePeriod':
      return item.duePeriodAmount;
    case 'totalDue':
      return item.dueNowAmount;
    case 'dueSince':
      return item.dueSince ? new Date(item.dueSince).getTime() : null;
  }
};

const sortedItems = computed(() => {
  const items = [...((itemsQuery.data.value?.items ?? []) as ReceivablesItem[])];
  const column = sortColumn.value;
  if (!(SORT_COLUMNS as readonly string[]).includes(column)) return items;
  const direction = sortDirection.value === 'desc' ? -1 : 1;
  return items.sort((left, right) => {
    const a = sortValue(left, column as SortColumn);
    const b = sortValue(right, column as SortColumn);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * direction;
  });
});

const escapeCsvCell = (value: string | number) => {
  const cell = String(value);
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
};

const formatCsvAmount = (value: number) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const handleExportCsv = async () => {
  const items = sortedItems.value;
  if (!items.length) return;

  const headers = [
    t('customer'),
    t('kind'),
    t('invoiceType'),
    t('booking'),
    t('invoice'),
    t('duePeriod'),
    t('totalDue'),
    t('dueSince'),
  ];

  const rows = items.map((item) => [
    props.formatCustomerName(item),
    item.invoiceId ? t('invoice') : t('booking'),
    item.invoiceType ? tGlobal(`invoiceTypes.${item.invoiceType}`, item.invoiceType) : '-',
    item.bookingNumber ?? '-',
    item.completeInvoiceNumber ?? '-',
    formatCsvAmount(item.duePeriodAmount),
    formatCsvAmount(item.dueNowAmount),
    item.dueSince ? formatDate(item.dueSince, 'dd.MM.yyyy') : '-',
  ]);

  const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
  await downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), t('exportCsvFilename'));
};
</script>

<i18n lang="json">
{
  "en": {
    "customer": "Customer",
    "kind": "Kind",
    "booking": "Booking",
    "invoice": "Invoice",
    "invoiceType": "Invoice type",
    "duePeriod": "Due in period",
    "totalDue": "Total due",
    "dueSince": "Due since",
    "noReceivables": "No receivables found",
    "exportCsv": "Export CSV",
    "exportCsvFilename": "receivables.csv"
  },
  "de": {
    "customer": "Kunde",
    "kind": "Art",
    "booking": "Buchung",
    "invoice": "Rechnung",
    "invoiceType": "Rechnungstyp",
    "duePeriod": "Fällig im Zeitraum",
    "totalDue": "Gesamt fällig",
    "dueSince": "Fällig seit",
    "noReceivables": "Keine Forderungen gefunden",
    "exportCsv": "CSV exportieren",
    "exportCsvFilename": "forderungen.csv"
  },
  "es": {
    "customer": "Cliente",
    "kind": "Tipo",
    "booking": "Reserva",
    "invoice": "Factura",
    "invoiceType": "Tipo de factura",
    "duePeriod": "Vence en el periodo",
    "totalDue": "Total vencido",
    "dueSince": "Vencido desde",
    "noReceivables": "No hay cobros pendientes",
    "exportCsv": "Exportar CSV",
    "exportCsvFilename": "cobros-pendientes.csv"
  }
}
</i18n>
